import { useState } from "react";
import { useAuthActions } from "@convex-dev/auth/react";
import { useQuery } from "convex/react";
import { ConvexError } from "convex/values";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@convex/_generated/api";

type Flow = "signIn" | "signUp";

function errorMessage(error: unknown): string {
  if (error instanceof ConvexError) {
    return typeof error.data === "string" ? error.data : "Sign-in failed.";
  }
  if (error instanceof Error) {
    // Convex Auth reports bad credentials as an opaque server error.
    return /InvalidAccountId|InvalidSecret|Invalid/i.test(error.message)
      ? "Incorrect username or password."
      : "Sign-in failed. Check the console for details.";
  }
  return "Sign-in failed.";
}

export default function SignIn() {
  const { signIn } = useAuthActions();
  const signupOpen = useQuery(api.users.signupOpen);
  const [flow, setFlow] = useState<Flow>("signIn");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const canSignUp = signupOpen === true;
  const activeFlow: Flow = canSignUp ? flow : "signIn";

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    const formData = new FormData(event.currentTarget);
    formData.set("flow", activeFlow);
    try {
      await signIn("password", formData);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="grid h-full place-items-center px-6 py-12 bg-[#fdfdfc] dark:bg-[#0f0f0e] text-[#222] dark:text-[#ededeb]">
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-sm rounded-2xl border border-[#d1d1cf] bg-[#fdfdfc] p-8 shadow-xs dark:border-[#2a2a28] dark:bg-[#141413] transition-colors overflow-hidden"
      >
        {/* Top meniscus highlight */}
        <div className="meniscus absolute top-0 left-0 w-full h-[1px] opacity-60" />

        <div className="mb-6">
          <h1 className="text-3xl font-light tracking-[-0.05em] text-[#222] dark:text-[#ededeb]">
            LLS
          </h1>
          <p className="mt-1 font-mono text-xs opacity-60 text-neutral-600 dark:text-neutral-400">
            Lecture Listening Scheme
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5 text-xs">
            <span className="font-mono text-[11px] uppercase tracking-wider text-neutral-600 dark:text-neutral-400">
              Username
            </span>
            {/* Named "email" for the Password provider; a bare username or the
                whole address both work. */}
            <input
              name="email"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              required
              className="rounded-lg border border-[#d1d1cf] bg-white px-3 py-2 text-sm text-[#222] outline-none transition-colors focus:border-[#222] dark:border-[#2a2a28] dark:bg-[#181817] dark:text-[#ededeb] dark:focus:border-[#ededeb]"
            />
          </label>

          <label className="flex flex-col gap-1.5 text-xs">
            <span className="font-mono text-[11px] uppercase tracking-wider text-neutral-600 dark:text-neutral-400">
              Password
            </span>
            <input
              name="password"
              type="password"
              autoComplete={
                activeFlow === "signUp" ? "new-password" : "current-password"
              }
              required
              minLength={8}
              className="rounded-lg border border-[#d1d1cf] bg-white px-3 py-2 text-sm text-[#222] outline-none transition-colors focus:border-[#222] dark:border-[#2a2a28] dark:bg-[#181817] dark:text-[#ededeb] dark:focus:border-[#ededeb]"
            />
          </label>

          <AnimatePresence>
            {error !== null && (
              <motion.p
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden rounded-xl border border-red-300 bg-red-500/10 px-3 py-2 font-mono text-xs text-red-600 dark:border-red-900 dark:text-red-400"
              >
                {error}
              </motion.p>
            )}
          </AnimatePresence>

          <motion.button
            type="submit"
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            disabled={submitting || signupOpen === undefined}
            className="mt-2 w-full cursor-pointer rounded-xl bg-[#222] px-4 py-2.5 text-xs font-semibold tracking-wide text-[#fdfdfc] shadow-xs transition-all hover:bg-[#111] hover:shadow-sm disabled:opacity-40 disabled:cursor-not-allowed dark:bg-[#ededeb] dark:text-[#141413] dark:hover:bg-white"
          >
            {activeFlow === "signUp" ? "Create account" : "Sign in"}
          </motion.button>
        </form>

        {canSignUp && (
          <button
            type="button"
            onClick={() => {
              setError(null);
              setFlow(flow === "signIn" ? "signUp" : "signIn");
            }}
            className="mt-4 block w-full text-center font-mono text-xs text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-300 transition-colors"
          >
            {flow === "signIn"
              ? "No account yet? Create yours."
              : "Already have an account? Sign in."}
          </button>
        )}

        {signupOpen === false && (
          <p className="mt-4 text-center font-mono text-[11px] text-neutral-400 dark:text-neutral-500">
            Signup is closed - accounts are invite-only.
          </p>
        )}

        {/* Bottom meniscus line */}
        <div className="meniscus absolute bottom-0 left-0 w-full h-[1px] opacity-30 pointer-events-none" />
      </motion.div>
    </div>
  );
}
